import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@ibsys/kit';
import type { AllDetailsFiltersState, AllDetailsSortState, DashboardQualitySettings } from '@/entities/dashboards';
import type { ConversationAttributeItem } from '@/entities/attributes';
import type { AllDetailsPages } from '../model/useAllDetailsPages';
import { AllDetailsTable, type AllDetailsSidePanelState } from './AllDetailsTable';

type Props = {
  /** Постраничный набор таблицы: окна выдачи, их общее число и пришедшее вместе с ними */
  pages: AllDetailsPages;
  blockNames: string[];
  /** Системная конфигурация атрибутов разговора (для форматирования ячеек атрибутных колонок) */
  availableAttributes: ConversationAttributeItem[];
  /** Настройки видимости и порядка колонок из uiSettings */
  columnSettings: DashboardQualitySettings['allDetailsColumns'];
  /** Сохранение настроек колонок (visibleColumns + columnOrder) */
  onChangeColumnSettings: (next: { visibleColumns: string[]; columnOrder: string[] }) => void;
  /** Текущая серверная сортировка таблицы. */
  sort: AllDetailsSortState | null;
  /** Колбэк изменения серверной сортировки. */
  onSortChange: (next: AllDetailsSortState | null) => void;
  /** Текущие серверные фильтры таблицы. */
  filters: AllDetailsFiltersState;
  /** Колбэк изменения серверных фильтров. */
  onFiltersChange: (next: AllDetailsFiltersState) => void;
  /** Открытие диалога строки; `openInNewTab` — пользователь кликнул с shift */
  onOpenConversation: (conversationId: number, openInNewTab: boolean) => void;
};

const INITIAL_SIDE_PANEL: AllDetailsSidePanelState = { activePanel: null, pinned: false };

/**
 * Карточка-обёртка таблицы всех деталей дашборда качества. Сортировка, фильтрация и постраничность
 * выполняются на бэкенде через query-параметры `sort`, `filter-*` и `limit`/`offset` эндпоинта
 * `GET /dashboard/quality-details`: прокрутка доводит до последней оценки выборки. Состав и порядок
 * колонок настраиваются через панель и сохраняются в
 * `uiSettings.dashboardQuality.allDetailsColumns`.
 */
export const AllDetailsCard: FC<Props> = ({
  pages,
  blockNames,
  availableAttributes,
  columnSettings,
  onChangeColumnSettings,
  sort,
  onSortChange,
  filters,
  onFiltersChange,
  onOpenConversation,
}) => {
  const { t } = useTranslation('qualityDashboard');

  // При смене набора динамических колонок (критериев выборки) пересоздаём компонент, чтобы
  // сбросить локальное состояние сортировки/скролла.
  const tableKey = blockNames.join(',');

  // Боковая панель живёт здесь, выше ключа таблицы: со сменой выборки дашборда таблица
  // пересоздаётся, а открытая панель, её закрепление и выставленная ширина остаются.
  const [sidePanel, setSidePanel] = useState<AllDetailsSidePanelState>(INITIAL_SIDE_PANEL);
  // Слияние через функцию: при откреплении грид зовёт колбэки закрепления и открытой панели
  // подряд, и второй не должен затереть первый
  const handleSidePanelChange = useCallback((patch: Partial<AllDetailsSidePanelState>) => {
    setSidePanel((prev) => ({ ...prev, ...patch }));
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('allDetails.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {!pages.isReady ? (
          <div className="flex justify-center py-8">
            <Spinner className="size-6" />
          </div>
        ) : (
          <AllDetailsTable
            key={tableKey}
            totalCount={pages.totalCount}
            pageSize={pages.pageSize}
            cacheVersion={pages.cacheVersion}
            fetchPage={pages.fetchPage}
            sampleRow={pages.sampleRow}
            blockNames={blockNames}
            availableColumns={pages.availableColumns}
            availableAttributes={availableAttributes}
            columnSettings={columnSettings}
            onChangeColumnSettings={onChangeColumnSettings}
            sort={sort}
            onSortChange={onSortChange}
            filters={filters}
            onFiltersChange={onFiltersChange}
            sidePanel={sidePanel}
            onSidePanelChange={handleSidePanelChange}
            onOpenConversation={onOpenConversation}
          />
        )}
      </CardContent>
    </Card>
  );
};
